import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Typography, Box, Tooltip } from '@mui/material'; 
import Grid from '@mui/material/Grid2';
import { formatEuro, formatPct } from '../../functions/formatNumber';
import { ConcepteIcon } from './IconMap';
import CustomOrderArrow from './IconosOrdenacion';
import { getColorForAmount } from '../../functions/colors';
import AppContext from '../AppContext';
import { formatSmartConceptPath, findNodeByNameOrCode } from '../../services/concepteGraphService';

const ConceptList = ({ concepts, tipus = 'mensual', viewMode = 'actual' }) => {
  const { conceptesTree } = useContext(AppContext);
  const [sortConfig, setSortConfig] = useState({ key: 'saldo', direction: 'asc' });
  const listConceptes = Object.values(concepts || {});
  const navigate = useNavigate();

  const filteredConcepts = listConceptes.filter((item) => {
    const isInversio = item.concepte?.tipus === 'INVERSIO';
    if (viewMode === 'inversiones') {
      return isInversio;
    }
    if (viewMode === 'estalvi') {
      return !isInversio;
    }
    return true;
  });

  // Función para manejar la ordenación
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const resolveTargetRoute = (userapp, anio, mes, concepteNom) => {
    const matchedNode = (conceptesTree && conceptesTree.length > 0)
      ? findNodeByNameOrCode(conceptesTree, concepteNom)
      : null;
    if (matchedNode && matchedNode.id) {
      const pathParam = matchedNode.conceptePath || matchedNode.path || matchedNode.codi;
      const pathQuery = pathParam ? `?path=${encodeURIComponent(pathParam)}` : '';
      if (tipus === 'mensual') {
        return `/${userapp}/dashboard/${anio}/mes/${mes}/node/${matchedNode.id}${pathQuery}`;
      } else if (tipus === 'anual') {
        return `/${userapp}/dashboard/${anio}/node/${matchedNode.id}${pathQuery}`;
      } else {
        return `/${userapp}/dashboard/node/${matchedNode.id}${pathQuery}`;
      }
    }

    if (tipus === 'mensual') {
      return `/${userapp}/dashboard/${anio}/mes/${mes}/concepte/${concepteNom}`;
    } else if (tipus === 'anual') {
      return `/${userapp}/dashboard/${anio}/concepte/${concepteNom}`;
    } else {
      return `/${userapp}/dashboard/concepte/${concepteNom}`;
    }
  };

  const gotoConcepte = (concepteData) => {
    navigate(resolveTargetRoute(concepteData.userapp, concepteData.anio, concepteData.mes, concepteData.concepte.nom));
  }; 

  const gotoConcepteAnyAnterior = (e, concepteData) => {
    e.stopPropagation();
    navigate(resolveTargetRoute(concepteData.userapp, concepteData.anio - 1, concepteData.mes, concepteData.concepte.nom));
  }; 

  // Función para ordenar los balances según el criterio
  const sortedBalances = [...filteredConcepts].sort((a, b) => {
    // Comprobar la clave de ordenación actual
    switch (sortConfig.key) {
      case 'concepte':
        return sortConfig.direction === 'asc'
          ? a.concepte.nom.localeCompare(b.concepte.nom)
          : b.concepte.nom.localeCompare(a.concepte.nom);

      case 'numMoviments':
        return sortConfig.direction === 'asc'
          ? a.importAgregat.numMoviments - b.importAgregat.numMoviments
          : b.importAgregat.numMoviments - a.importAgregat.numMoviments;

      case 'saldo':
        return sortConfig.direction === 'asc'
          ? a.importAgregat.saldo - b.importAgregat.saldo
          : b.importAgregat.saldo - a.importAgregat.saldo;

      case 'desviamentMitja':
        return (a.variacioMitjaAnys && b.variacioMitjaAnys)
          ? (
            sortConfig.direction === 'asc'
            ? a.variacioMitjaAnys.saldo - b.variacioMitjaAnys.saldo
            : b.variacioMitjaAnys.saldo - a.variacioMitjaAnys.saldo
          )
          : 0;
      case 'desviamentAnyAnt':
        return (a.variacioAnyAnterior && b.variacioAnyAnterior)
          ? (
            sortConfig.direction === 'asc'
            ? a.variacioAnyAnterior.saldo - b.variacioAnyAnterior.saldo
            : b.variacioAnyAnterior.saldo - a.variacioAnyAnterior.saldo
          )
          : 0;
    
      default:
        return 0; // Si no hay un criterio de ordenación válido
  }
  });


  return (
    <Paper sx={{ padding: '20px' }}>
        {/* Cabecera del Grid */}
        <Grid container spacing={2} alignItems="center" sx={{ 
          marginBottom: 2, 
          fontWeight: 'bold', 
          backgroundColor: '#f0f0f0', // Fondo claro
          color: '#000', // Texto oscuro
          paddingTop: '2px', 
          paddingBottom: '2px',
          paddingLeft: '10px',
          paddingRight: '10px',
          borderBottom: '2px solid #ddd' // Borde inferior
        }}>
          <Grid size={{xs: 12, sm: 4 }}>
            <Typography variant="body2">
              <span onClick={() => handleSort('concepte')}>
                Concepte {sortConfig.key === 'concepte' && <CustomOrderArrow direction={sortConfig.direction}/> }
              </span>{' '}
              <span style={{ fontSize: '0.8rem', color: 'gray' }} onClick={() => handleSort('numMoviments')}>
                (Num.Moviments) {sortConfig.key === 'numMoviments' && <CustomOrderArrow direction={sortConfig.direction}/> }
              </span>
            </Typography>
          </Grid>
          <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="center" sx={{ gap: '1px' }}>
            <Typography variant="caption">
              Pes
            </Typography>
            <Typography variant="caption">
              Mitja
            </Typography>
          </Grid>
          <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="flex-end" sx={{ gap: '1px' }}>
            <Typography variant="body2" onClick={() => handleSort('saldo')}>
              Saldo Concepte {sortConfig.key === 'saldo' && <CustomOrderArrow direction={sortConfig.direction}/> }
            </Typography>
            <Typography variant="caption" color="gray">
              <span onClick={() => handleSort('desviamentMitja')}>
                Desviament Mitjana {sortConfig.key === 'desviamentMitja' && <CustomOrderArrow direction={sortConfig.direction}/> }
              </span>{' / '}
              <span onClick={() => handleSort('desviamentAnyAnt')}>
                Any Anterior {sortConfig.key === 'desviamentAnyAnt' && <CustomOrderArrow direction={sortConfig.direction}/> }
              </span>
            </Typography>
          </Grid>
        </Grid>    
        
        {/* List Conceptes */}
        {sortedBalances.length === 0 ? (
          <Box sx={{ padding: '20px', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {viewMode === 'inversiones' 
                ? "No hi ha conceptes d'inversió en aquest període" 
                : "No hi ha conceptes per mostrar"}
            </Typography>
          </Box>
        ) : (
          sortedBalances.map((value) =>  {
          const isInversio = value.concepte?.tipus === 'INVERSIO';
          
          const colorSaldo = getColorForAmount(value.importAgregat.saldo, isInversio);
          const colorDesv = value.variacioMitjaAnys ? getColorForAmount(value.variacioMitjaAnys.saldo, isInversio) : 'green';
          const colorDesvAA = value.variacioAnyAnterior ? getColorForAmount(value.variacioAnyAnterior.saldo, isInversio) : 'green';
          const { displayPath, fullBreadcrumb } = formatSmartConceptPath(value.concepte.nom, value.concepte, conceptesTree);

          return (
            <Grid container key={value.concepte.nom} spacing={2} alignItems="center" 
              onClick={() => gotoConcepte(value)}
              sx={{ 
                marginBottom: 1,
                paddingLeft: '10px',
                paddingRight: '10px',
                transition: 'background-color 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)', // Cambia el color de fondo al pasar el ratón
                },
              }}>
              {/* Columna izquierda: Concepte, número de movimientos y concepte path intel·ligent */}
              <Grid size={{xs: 12, sm: 4 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ConcepteIcon concept={value.concepte.nom}/>
                  <Typography component="span" color="gray" fontSize="small">
                    ({value.importAgregat.numMoviments})
                  </Typography>
                </Box>
                {displayPath && (
                  <Tooltip title={`Ruta Jeràrquica: ${fullBreadcrumb || displayPath}`}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.68rem',
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        maxWidth: '220px',
                        pl: '26px'
                      }}
                    >
                      {displayPath}
                    </Typography>
                  </Tooltip>
                )}
              </Grid>

              {/* Columna central: Pes y Mitja */}
              <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="center" sx={{ gap: '1px' }}>
                <Typography variant="caption">
                  {formatPct(value.percentatge)}
                </Typography>
                <Typography variant="caption">
                  {formatEuro(value.importAgregat.mitjaSaldo)}
                </Typography>            
              </Grid>

              {/* Columna derecha: Saldo del concepte y desv mitjana */}
              <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="flex-end" sx={{ gap: '1px' }}>
                <Typography variant="body1" fontWeight="bold" color={colorSaldo} >
                  {formatEuro(value.importAgregat.saldo)}
                </Typography>
                <Typography variant="caption">
                  <span style={{ color: colorDesv }}>
                    {formatEuro(value.variacioMitjaAnys ? value.variacioMitjaAnys.saldo: 0)}
                  </span>{' / '}
                  <Tooltip title="Fes clic per navegar a l'any anterior" arrow>
                    <Box 
                      component="span"
                      sx={{ 
                        color: colorDesvAA,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }} 
                      onClick={(e) => gotoConcepteAnyAnterior(e, value)}
                    >
                      {formatEuro(value.variacioAnyAnterior ? value.variacioAnyAnterior.saldo: 0)}
                    </Box>
                  </Tooltip>
                </Typography>
              </Grid>
            </Grid>
          );
        })
      )}
    </Paper>
    );
};

export default ConceptList;

